import { filter, merge, tap } from 'rxjs';
import { store } from '@redux/store';
import { onDocumentKeyDown$ } from 'layout/redux/observables';
import uiSlice from '../redux/uiStateSlice';
import modalService from '../services/modalService';
import Overlay from './overlay';

const modal$ = modalService.getHtml();

const modalHtml = {
  modalContainer: document.querySelector('.modal__box'),
  previousHtml: null,
  previousCallback: null,
  escapeSubscription: null,
};

const disconnectedCallbacks = new Map();
const registeredCallbacks = new Map();
const overlay = new Overlay();

const isEscapeKeyPressed = (event) => event.key === 'Escape';

const onEscapeCloseModal$ = onDocumentKeyDown$.pipe(
  filter(() => !!modalHtml.modalContainer),
  filter(() => modalHtml.modalContainer.dataset.modalExpanded === 'true'),
  filter(isEscapeKeyPressed)
);

const onDisconnected = (callbackName) => {
  const callbacksDisconnected = disconnectedCallbacks.get(callbackName) ?? [];
  callbacksDisconnected
    .filter((fn) => typeof fn === 'function')
    .forEach((fn) => fn());

  if (disconnectedCallbacks.has(callbackName)) {
    disconnectedCallbacks.delete(callbackName);
    registeredCallbacks.delete(callbackName);
  }
};

const closeModal = () => {
  const modalInformation = store.getState().uiState?.modalInformation;
  const callbackName = modalInformation?.name;

  modalHtml.escapeSubscription?.unsubscribe();
  modalHtml.escapeSubscription = null;

  onDisconnected(callbackName);

  modalHtml.modalContainer.classList.remove('modal--expanded');
  modalHtml.modalContainer.removeAttribute('data-modal-expanded');

  overlay.remove();

  modalHtml.modalContainer.innerHTML = null;
  modalHtml.previousCallback = null;
  modalHtml.previousHtml = null;

  store.dispatch(uiSlice.actions.clearModal());

  const event = new CustomEvent('modalClosed', {
    detail: { modalInformation },
  });

  setTimeout(() => {
    document.body.dispatchEvent(event);
  }, 100);
};

const disconnectCallback = (data) => {
  const { name, fn } = data;
  const existingCallbacks = disconnectedCallbacks.get(name) ?? [];
  disconnectedCallbacks.set(name, [fn, ...existingCallbacks]);
};

const openModal = (html) => {
  const modalInformation = store.getState().uiState?.modalInformation;
  const callbackName = modalInformation?.name;
  const previousCallbackName = modalInformation?.previousName;

  const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

  modalHtml.previousCallback = callbackName;
  modalHtml.previousHtml = modalHtml.modalContainer.innerHTML?.trim();

  if (modalHtml.previousHtml) {
    onDisconnected(previousCallbackName);
  }

  modalHtml.modalContainer.innerHTML = html.value;
  modalHtml.modalContainer.classList.add('modal--expanded');
  modalHtml.modalContainer.setAttribute('data-modal-expanded', 'true');

  overlay.create();

  callbacksRegistered
    .filter((fn) => typeof fn === 'function')
    .forEach((fn) => fn());

  modalHtml.escapeSubscription?.unsubscribe();
  modalHtml.escapeSubscription = onEscapeCloseModal$.subscribe(() => {
    modalService.clearHtml();
  });
};

const registerCallback = (data) => {
  const { name, fn } = data;
  const existingCallbacks = registeredCallbacks.get(name) ?? [];
  registeredCallbacks.set(name, [fn, ...existingCallbacks]);
};

const setPreviousHtml = () => {
  onDisconnected(modalHtml.previousCallback);
  modalHtml.modalContainer.innerHTML = null;
  modalService.setHtml({ payload: modalHtml.previousHtml });
};

const close = modal$.pipe(
  filter((data) => data.action === 'clearHtml'),
  tap(closeModal)
);

const disconnect = modal$.pipe(
  filter((data) => data.action === 'onHtmlRemoved'),
  tap(disconnectCallback)
);

const open = modal$.pipe(
  filter((data) => data.action === 'setHtml'),
  tap(openModal)
);

const register = modal$.pipe(
  filter((data) => data.action === 'onHtmlReady'),
  tap(registerCallback)
);

const previous = modal$.pipe(
  filter((data) => data.action === 'setPreviousHtml'),
  tap(setPreviousHtml)
);

merge(close, disconnect, open, register, previous).subscribe();
